(() => {
  const register = (name, initializer) => {
    if (window.SafeWalkV51Product?.register) window.SafeWalkV51Product.register(name, initializer);
    else (window.SafeWalkV51ProductQueue ||= []).push([name, initializer]);
  };

  register('gallery', ({ root, listen }) => {
    const gallery = root.querySelector('[data-gallery-enabled], [data-swv51-gallery], .swv5-gallery');
    if (!gallery || gallery.dataset.galleryEnabled === 'false') return;
    const stage = gallery.querySelector('[data-gallery-stage]');
    const thumbsViewport = gallery.querySelector('[data-gallery-thumbs]');
    const previousButton = gallery.querySelector('[data-gallery-prev]');
    const nextButton = gallery.querySelector('[data-gallery-next]');
    const thumbPreviousButton = gallery.querySelector('[data-gallery-thumb-prev]');
    const thumbNextButton = gallery.querySelector('[data-gallery-thumb-next]');
    const galleryMode = root.dataset.galleryMode || 'harness_assets';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const variantsNode = root.querySelector('[data-swv5-variants]');
    let variants = [];
    try {
      const parsed = variantsNode ? JSON.parse(variantsNode.textContent || '[]') : [];
      variants = Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      variants = [];
    }
    const variantMediaIds = new Set(
      variants
        .map((variant) => variant.featured_media?.id)
        .filter(Boolean)
        .map(String)
    );
    const nativeMediaIds = new Set(
      Array.from(gallery.querySelectorAll('[data-media-id]'))
        .map((frame) => String(frame.dataset.mediaId || ''))
        .filter(Boolean)
    );
    const virtualMedia = new Map();
    let activeMediaId = gallery.querySelector('[data-media-id].is-active')?.dataset.mediaId
      || gallery.querySelector('[data-media-id]')?.dataset.mediaId
      || '';
    let lastNativeMediaId = nativeMediaIds.has(String(activeMediaId)) ? String(activeMediaId) : '';
    const visibleThumbs = () => Array.from(gallery.querySelectorAll('[data-thumb-id]')).filter((thumb) => !thumb.hidden);
    const hydrateFrame = (frame) => {
      if (!frame) return;
      const image = frame.querySelector('img[data-src]');
      if (image) {
        if (image.dataset.srcset) {
          image.srcset = image.dataset.srcset;
          image.removeAttribute('data-srcset');
        }
        image.src = image.dataset.src;
        image.removeAttribute('data-src');
      }
      const videoSource = frame.querySelector('video source[data-src]');
      if (videoSource) {
        videoSource.src = videoSource.dataset.src;
        videoSource.removeAttribute('data-src');
        videoSource.closest('video')?.load();
      }
    };
    const hydrateNeighbors = (frames, activeIndex) => {
      [activeIndex, activeIndex - 1, activeIndex + 1].forEach((index) => {
        if (index >= 0 && index < frames.length) hydrateFrame(frames[index]);
      });
    };
    const setMedia = (mediaId, centerThumb = true, source = 'api') => {
      if (!mediaId) return false;
      const id = String(mediaId);
      const frames = Array.from(gallery.querySelectorAll('[data-media-id]'));
      const activeIndex = frames.findIndex((frame) => frame.dataset.mediaId === id);
      if (activeIndex < 0) return false;
      hydrateNeighbors(frames, activeIndex);
      const changed = activeMediaId !== id;
      activeMediaId = id;
      if (nativeMediaIds.has(id)) lastNativeMediaId = id;
      frames.forEach((frame) => {
        const active = frame.dataset.mediaId === id;
        frame.classList.toggle('is-active', active);
        frame.setAttribute('aria-hidden', String(!active));
        frame.toggleAttribute('inert', !active);
        const video = frame.querySelector('video');
        if (!video) return;
        if (active && !reducedMotion.matches) video.play().catch(() => {});
        else video.pause();
      });
      gallery.querySelectorAll('[data-thumb-id]').forEach((thumb) => {
        const active = thumb.dataset.thumbId === id;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      const activeThumb = Array.from(gallery.querySelectorAll('[data-thumb-id]')).find((thumb) => thumb.dataset.thumbId === id);
      if (centerThumb && activeThumb && thumbsViewport) {
        const viewportRect = thumbsViewport.getBoundingClientRect();
        const thumbRect = activeThumb.getBoundingClientRect();
        const offset = thumbRect.left + thumbRect.width / 2 - viewportRect.left - viewportRect.width / 2;
        thumbsViewport.scrollBy({ left: offset, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
      if (changed) {
        root.dispatchEvent(new CustomEvent('swv5:gallery-change', {
          bubbles: true,
          detail: { mediaId: id, virtual: virtualMedia.has(id), source }
        }));
      }
      return true;
    };
    const restoreNative = (centerThumb = false) => {
      const visibleNativeThumb = visibleThumbs().find((thumb) => nativeMediaIds.has(String(thumb.dataset.thumbId || '')));
      const firstNativeFrame = Array.from(gallery.querySelectorAll('[data-media-id]'))
        .find((frame) => nativeMediaIds.has(String(frame.dataset.mediaId || '')));
      return setMedia(
        lastNativeMediaId || visibleNativeThumb?.dataset.thumbId || firstNativeFrame?.dataset.mediaId,
        centerThumb,
        'restore'
      );
    };
    const unregisterVirtualMedia = (mediaId) => {
      const id = String(mediaId || '');
      const entry = virtualMedia.get(id);
      if (!entry) return false;
      if (activeMediaId === id) restoreNative(false);
      entry.thumb?.removeEventListener('click', entry.onThumbClick);
      entry.frame?.remove();
      entry.thumb?.remove();
      virtualMedia.delete(id);
      return true;
    };
    const registerVirtualMedia = ({
      id,
      frame,
      thumb,
      preserveOnVariantChange = false
    } = {}) => {
      const mediaId = String(id || '');
      if (!mediaId || nativeMediaIds.has(mediaId) || virtualMedia.has(mediaId) || !frame) return false;
      frame.dataset.mediaId = mediaId;
      frame.dataset.mediaType = 'virtual';
      frame.classList.add('swv5-gallery__frame');
      frame.classList.remove('is-active');
      frame.setAttribute('aria-hidden', 'true');
      frame.setAttribute('inert', '');
      stage?.insertBefore(frame, stage.querySelector('.swv5-gallery__nav'));
      let onThumbClick = null;
      if (thumb && thumbsViewport) {
        thumb.dataset.thumbId = mediaId;
        thumb.classList.add('swv5-gallery__thumb');
        thumb.setAttribute('aria-pressed', 'false');
        onThumbClick = () => setMedia(mediaId, true, 'thumb');
        thumb.addEventListener('click', onThumbClick);
        thumbsViewport.append(thumb);
      }
      virtualMedia.set(mediaId, { frame, thumb, onThumbClick, preserveOnVariantChange });
      return true;
    };
    const updateThumbVisibility = (variant) => {
      if (galleryMode !== 'product_media' || !variantMediaIds.size) return;
      const currentVariantMediaId = variant?.featured_media?.id ? String(variant.featured_media.id) : '';
      gallery.querySelectorAll('[data-thumb-id]').forEach((thumb) => {
        const id = String(thumb.dataset.thumbId || '');
        thumb.hidden = variantMediaIds.has(id) && currentVariantMediaId && id !== currentVariantMediaId;
      });
    };
    const move = (direction) => {
      const thumbs = visibleThumbs();
      if (!thumbs.length) return;
      const currentIndex = Math.max(0, thumbs.findIndex((thumb) => thumb.dataset.thumbId === activeMediaId));
      const nextIndex = (currentIndex + direction + thumbs.length) % thumbs.length;
      setMedia(thumbs[nextIndex].dataset.thumbId, true, 'navigation');
    };
    const scrollThumbs = (direction) => {
      if (!thumbsViewport) return;
      const amount = Math.max(120, Math.round(thumbsViewport.clientWidth * 0.72));
      thumbsViewport.scrollBy({ left: amount * direction, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    };
    if (stage) {
      let startX = 0;
      let startY = 0;
      let startTime = 0;
      let tracking = false;
      listen(stage, 'pointerdown', (event) => {
        if (!event.isPrimary || !window.matchMedia('(max-width: 989px)').matches) {
          tracking = false;
          return;
        }
        startX = event.clientX;
        startY = event.clientY;
        startTime = Date.now();
        tracking = true;
      });
      listen(stage, 'pointerup', (event) => {
        if (!tracking || !event.isPrimary) return;
        tracking = false;
        const deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        if (Date.now() - startTime > 800 || Math.abs(deltaX) < 44 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;
        move(deltaX < 0 ? 1 : -1);
      });
      listen(stage, 'pointercancel', () => { tracking = false; });
      listen(stage, 'dragstart', (event) => event.preventDefault());
    }
    gallery.querySelectorAll('[data-thumb-id]').forEach((button) => {
      listen(button, 'click', () => setMedia(button.dataset.thumbId, true, 'thumb'));
    });
    listen(previousButton, 'click', () => move(-1));
    listen(nextButton, 'click', () => move(1));
    listen(thumbPreviousButton, 'click', () => scrollThumbs(-1));
    listen(thumbNextButton, 'click', () => scrollThumbs(1));
    listen(root, 'swv5:variant-change', (event) => {
      const variant = event.detail?.variant;
      updateThumbVisibility(variant);
      if (!event.detail?.colorChanged) return;
      const colorMediaId = event.detail?.colorMediaId;
      const variantMediaId = variant?.featured_media?.id || '';
      if (virtualMedia.get(activeMediaId)?.preserveOnVariantChange) {
        const nextNativeId = [colorMediaId, variantMediaId]
          .map((id) => String(id || ''))
          .find((id) => nativeMediaIds.has(id));
        if (nextNativeId) lastNativeMediaId = nextNativeId;
        return;
      }
      if (!colorMediaId || !setMedia(colorMediaId, true, 'variant')) setMedia(variantMediaId, true, 'variant');
    });
    root.querySelectorAll('[data-color-media-id]').forEach((input) => {
      listen(input, 'change', () => {
        if (!root.swv5Purchase && input.checked) setMedia(input.dataset.colorMediaId, true);
      });
    });
    const currentVariant = variants.find((variant) => Number(variant.id) === Number(root.querySelector('[data-variant-id]')?.value)) || null;
    updateThumbVisibility(currentVariant);
    setMedia(activeMediaId, false);
    const api = {
      registerVirtualMedia,
      unregisterVirtualMedia,
      setMedia,
      restoreNative,
      isActive: (mediaId) => activeMediaId === String(mediaId || ''),
      get activeMediaId() {
        return activeMediaId;
      }
    };
    root.swv5Gallery = api;
    root.dispatchEvent(new CustomEvent('swv5:gallery-ready', { bubbles: true, detail: { api } }));

    return () => {
      Array.from(virtualMedia.keys()).forEach(unregisterVirtualMedia);
      if (root.swv5Gallery === api) delete root.swv5Gallery;
      gallery.querySelectorAll('video').forEach((video) => video.pause());
    };
  });
})();
